import type React from "react";
import { createContext, useCallback, useContext, useState } from "react";
import type { FieldType } from "../interfaces/dynamicForm.interface";
import type {
	ElementRendererFunction,
	ElementRendererRegistry,
	FieldRendererFunction,
	FieldRendererRegistry,
	RendererContextValue,
	RendererProviderProps,
	RendererSystemConfig,
} from "../interfaces/renderer.interface";
import type { UtiliSourceCMSElementType } from "../interfaces/UtiliSouceCMS.interface";

// Create the renderer context
const RendererContext = createContext<RendererContextValue | undefined>(
	undefined,
);

// Renderer Provider component
export const RendererProvider: React.FC<RendererProviderProps> = ({
	children,
	fieldRenderers: customFieldRenderers = {},
	elementRenderers: customElementRenderers = {},
	defaultRenderers,
}) => {
	// Initialize with empty renderers or provided defaults
	const initialFieldRenderers = defaultRenderers?.fieldRenderers || {};
	const initialElementRenderers = defaultRenderers?.elementRenderers || {};

	// Merge with custom renderers
	const [fieldRenderers, setFieldRenderers] = useState<FieldRendererRegistry>({
		...initialFieldRenderers,
		...customFieldRenderers,
	});

	const [elementRenderers, setElementRenderers] =
		useState<ElementRendererRegistry>({
			...initialElementRenderers,
			...customElementRenderers,
		});

	// Register field renderer
	const registerFieldRenderer = useCallback(
		(fieldType: FieldType, renderer: FieldRendererFunction) => {
			setFieldRenderers((prev) => ({
				...prev,
				[fieldType]: renderer,
			}));
		},
		[],
	);

	// Register element renderer
	const registerElementRenderer = useCallback(
		(
			elementType: UtiliSourceCMSElementType,
			renderer: ElementRendererFunction,
		) => {
			setElementRenderers((prev) => ({
				...prev,
				[elementType]: renderer,
			}));
		},
		[],
	);

	// Unregister field renderer
	const unregisterFieldRenderer = useCallback((fieldType: FieldType) => {
		setFieldRenderers((prev) => {
			const newRenderers = { ...prev };
			delete newRenderers[fieldType];
			return newRenderers;
		});
	}, []);

	// Unregister element renderer
	const unregisterElementRenderer = useCallback(
		(elementType: UtiliSourceCMSElementType) => {
			setElementRenderers((prev) => {
				const newRenderers = { ...prev };
				delete newRenderers[elementType];
				return newRenderers;
			});
		},
		[],
	);

	const contextValue: RendererContextValue = {
		fieldRenderers,
		elementRenderers,
		registerFieldRenderer,
		registerElementRenderer,
		unregisterFieldRenderer,
		unregisterElementRenderer,
	};

	return (
		<RendererContext.Provider value={contextValue}>
			{children}
		</RendererContext.Provider>
	);
};

/**
 * Returns renderer context when inside a {@link RendererProvider}, otherwise `undefined`.
 * Safe to call unconditionally (e.g. to merge with parent renderers).
 */
export function useOptionalRendererContext(): RendererContextValue | undefined {
	return useContext(RendererContext);
}

// Hook to use renderer context
export const useRendererContext = (): RendererContextValue => {
	const context = useOptionalRendererContext();
	if (!context) {
		throw new Error(
			"useRendererContext must be used within a RendererProvider",
		);
	}
	return context;
};

// Renderer system class for programmatic access
export class RendererSystem {
	private fieldRenderers: FieldRendererRegistry;
	private elementRenderers: ElementRendererRegistry;

	constructor(config?: RendererSystemConfig) {
		this.fieldRenderers = config?.fieldRenderers || {};
		this.elementRenderers = config?.elementRenderers || {};
	}

	// Register field renderer
	registerFieldRenderer(
		fieldType: FieldType,
		renderer: FieldRendererFunction,
	): void {
		this.fieldRenderers[fieldType] = renderer;
	}

	// Register element renderer
	registerElementRenderer(
		elementType: UtiliSourceCMSElementType,
		renderer: ElementRendererFunction,
	): void {
		this.elementRenderers[elementType] = renderer;
	}

	// Unregister field renderer
	unregisterFieldRenderer(fieldType: FieldType): void {
		delete this.fieldRenderers[fieldType];
	}

	// Unregister element renderer
	unregisterElementRenderer(elementType: UtiliSourceCMSElementType): void {
		delete this.elementRenderers[elementType];
	}

	// Get field renderer
	getFieldRenderer(fieldType: FieldType): FieldRendererFunction | undefined {
		return this.fieldRenderers[fieldType];
	}

	// Get element renderer
	getElementRenderer(
		elementType: UtiliSourceCMSElementType,
	): ElementRendererFunction | undefined {
		return this.elementRenderers[elementType];
	}

	// Check if field renderer exists
	hasFieldRenderer(fieldType: FieldType): boolean {
		return fieldType in this.fieldRenderers;
	}

	// Check if element renderer exists
	hasElementRenderer(elementType: UtiliSourceCMSElementType): boolean {
		return elementType in this.elementRenderers;
	}

	// Get all field renderers
	getAllFieldRenderers(): FieldRendererRegistry {
		return { ...this.fieldRenderers };
	}

	// Get all element renderers
	getAllElementRenderers(): ElementRendererRegistry {
		return { ...this.elementRenderers };
	}

	// Reset to empty renderers
	reset(): void {
		this.fieldRenderers = {};
		this.elementRenderers = {};
	}

	// Update renderers
	updateRenderers(config: RendererSystemConfig): void {
		this.fieldRenderers = { ...this.fieldRenderers, ...config.fieldRenderers };
		this.elementRenderers = {
			...this.elementRenderers,
			...config.elementRenderers,
		};
	}
}

// Create default renderer system instance
export const defaultRendererSystem = new RendererSystem();
